import type { CustomCellEditorProps } from '../react.js';

import { type CurrencyCodeType } from '@mint-lib/currencies-helpers';
import {
  CurrencyFieldProps,
  useFormattedCurrencyInput,
} from '@mint-lib/formatting-hooks';
import { useFlags } from '@mint-lib/routing-context';
import { Input } from '@mint-lib/tw-components';

import { useStartFromEventKey } from '../hooks/useStartFromEventKey.js';

export interface BudgetEditorProps extends CustomCellEditorProps {
  /**
   * The currency code
   */
  currency: CurrencyCodeType;
  /**
   * The maximum number of decimal places allowed
   */
  maxDecimals?: number;
  /**
   * Additional props to pass to the input
   */
  InputProps?: Omit<
    Partial<CurrencyFieldProps>,
    'value' | 'onChange' | 'onBlur'
  >;
}

export const BudgetEditor = (props: BudgetEditorProps) => {
  const { onValueChange, value, currency, maxDecimals, eventKey, InputProps } =
    props;

  const ff = useFlags('@mint/arm', ['decimalFormattingOnCellEdit']);

  const startValue = useStartFromEventKey(eventKey, value, onValueChange);

  const inputProps = useFormattedCurrencyInput({
    ...InputProps,
    value: startValue === '' ? null : startValue,
    currency,
    onChange: onValueChange,
    maxDecimals,
    autoFocus: true,
    size: 'small',
    // TODO(PDT-102307): remove this prop together with the
    // `decimalFormattingOnCellEdit` feature flag.
    preserveRawValueOnEdit: ff.decimalFormattingOnCellEdit.enabled,
  });

  return (
    <Input
      aria-label="budget-editor"
      data-dd-action-name="Grid cell: edit budget"
      className="w-full border-none text-right focus:outline-none"
      {...inputProps}
    />
  );
};
